import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { PanoDurumService } from '@/src/app/uygulamalar/hmi-pano/services/pano-durum.service';

@Component({
  selector: 'personel-avatar',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="pa-wrap" [style.width.px]="boyut()+16" [style.height.px]="boyut()+16">
      @if (ringGoster()) {
        <div class="pa-ring"></div>
      }
      <div class="pa-avatar" [style.width.px]="boyut()" [style.height.px]="boyut()">
        @if (resimUrl()) {
          <img [src]="resimUrl()" style="width:100%;height:100%;object-fit:cover;object-position:center top;display:block;border-radius:50%" (error)="resimUrl.set('')" />
        } @else {
          <div class="pa-harf" [style.font-size.px]="boyut()/2.5">{{ basHarf() }}</div>
        }
      </div>
      @if (durumGoster()) {
        <div class="pa-badge pa-badge--durum" [class.pa-badge--online]="cevrimici()"
             [style.width.px]="badgeBoyut()" [style.height.px]="badgeBoyut()"
             [style.bottom.px]="durumPos().y" [style.right.px]="durumPos().x"></div>
      }
      @if (bagliUye()) {
        <div class="pa-badge pa-badge--link"
             [style.width.px]="badgeBoyut()" [style.height.px]="badgeBoyut()"
             [style.bottom.px]="linkPos().y" [style.left.px]="linkPos().x">
          <svg [attr.width]="badgeBoyut()*0.5" [attr.height]="badgeBoyut()*0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
          </svg>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: inline-block; }
    .pa-wrap {
      position: relative; display: flex;
      align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .pa-ring {
      position: absolute; inset: 0; border-radius: 50%;
      background: conic-gradient(from 0deg, #3b82f6, #6366f1, #8b5cf6, #3b82f6);
      opacity: .55; transition: opacity .3s;
    }
    :host:hover .pa-ring { opacity: .7; }
    .pa-avatar {
      position: absolute; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%; box-sizing: border-box;
      border: 3px solid rgba(255,255,255,.15); overflow: hidden;
      background: linear-gradient(135deg, #1e293b, #334155);
      box-shadow: 0 4px 16px rgba(0,0,0,.3);
      transition: transform .35s cubic-bezier(.22,1,.36,1);
    }
    :host:hover .pa-avatar { transform: translate(-50%, -50%) scale(1.06); }
    .pa-harf {
      width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, #ec4899, #f97316);
      color: #fff; font-weight: 700; text-transform: uppercase;
    }
    .pa-badge {
      position: absolute; border-radius: 50%;
      border: 2px solid #0f172a; z-index: 2;
      display: flex; align-items: center; justify-content: center;
      transition: background .3s, box-shadow .3s, transform .3s;
    }
    :host:hover .pa-badge { transform: scale(1.1); }
    .pa-badge--durum {
      background: #6b7280;
    }
    .pa-badge--online {
      background: #22c55e;
      box-shadow: 0 0 6px rgba(34,197,94,.6);
    }
    .pa-badge--link {
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: #fff;
      box-shadow: 0 2px 6px rgba(59,130,246,.4);
    }
  `],
})
export class PersonelAvatarComponent extends FikoYTKokBilesenComponent {
  kullaniciId = input<string>();
  ad = input<string>();
  soyad = input<string>();
  boyut = input(72);
  ringGoster = input(true);
  durumGoster = input(true);
  bagliUye = input(false);

  private req = inject(FikoYTRequestService);
  private panoDurum = inject(PanoDurumService, { optional: true });
  resimUrl = signal('');
  private uyeId = signal('');

  cevrimici = computed(() => {
    if (!this.panoDurum) return false;
    const kid = this.kullaniciId() || this.uyeId();
    const ad = this.ad() || '';
    const soyad = this.soyad() || '';
    const aktif = this.panoDurum.taninanKisi();
    if (kid && this.panoDurum.sonPersonel()?._id === kid) return true;
    if (aktif.isim && ad && aktif.isim.toLowerCase() === ad.toLowerCase() &&
        aktif.soyIsim && soyad && aktif.soyIsim.toLowerCase() === soyad.toLowerCase()) return true;
    const girisler = this.panoDurum.sonGirisler();
    return girisler.some(g => g.isim?.toLowerCase() === ad.toLowerCase() && g.soyisim?.toLowerCase() === soyad.toLowerCase());
  });

  basHarf = computed(() => {
    const a = this.ad() || '';
    const s = this.soyad() || '';
    const tam = (a + ' ' + s).trim();
    if (!tam) return '?';
    const parcalar = tam.split(/\s+/);
    if (parcalar.length >= 2)
      return ((parcalar[0]?.[0] || '') + (parcalar[parcalar.length - 1]?.[0] || '')).toUpperCase();
    return (parcalar[0]?.[0] || '?').toUpperCase();
  });

  badgeBoyut = computed(() => Math.max(12, Math.min(18, this.boyut() * 0.22)));

  durumPos = computed(() => {
    const r = (this.boyut() + 16) / 2;
    const aci = 315 * (Math.PI / 180);
    return { x: r - r * Math.cos(aci) - this.badgeBoyut() / 2, y: r - r * Math.sin(aci) - this.badgeBoyut() / 2 };
  });

  linkPos = computed(() => {
    const r = (this.boyut() + 16) / 2;
    const aci = 225 * (Math.PI / 180);
    return { x: r + r * Math.cos(aci) - this.badgeBoyut() / 2, y: r - r * Math.sin(aci) - this.badgeBoyut() / 2 };
  });

  constructor() {
    super();
    effect(() => {
      const kid = this.kullaniciId();
      const ad = this.ad();
      const soyad = this.soyad();
      if (kid) {
        this.resimCek({ _id: kid });
      } else if (ad) {
        const match: any = { isim: ad };
        if (soyad) match.soyIsim = soyad;
        this.resimCek(match);
      }
    });
  }

  private async resimCek(match: any) {
    try {
      const sonuc = await this.req.aggregate<any>('FikoYT.sistem.DB.goUyeler.aggregate', [
        { $match: { ...match, silindi: { $ne: true } } },
        { $project: { resimId: { $ifNull: ['$profilResmi', { $ifNull: ['$veri.resimId', ''] }] } } },
        { $limit: 1 },
      ]);
      if (sonuc?.[0]?._id) this.uyeId.set(sonuc[0]._id);
      const resId = sonuc?.[0]?.resimId;
      if (resId) {
        const fapiUrl = this.FikoYT?.fapiUrl || '';
        if (fapiUrl) {
          this.resimUrl.set(
            fapiUrl + '/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.' + resId + '.resim?boyut=' + this.boyut() + 'x?&thumbnail=true'
          );
        }
      }
    } catch {}
  }
}
